import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ALL_PAYMENT_METHODS } from '@lims/shared-types';
import { clientsApi, invoicesApi, paymentsApi } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  DateField,
  Form,
  FormSection,
  NumberField,
  PageHeader,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { FinanceMutationError } from './finance-errors.js';

interface AllocationDraft {
  readonly key: string;
  invoiceId: string | null;
  amount: number | null;
}

let allocationKeySeed = 0;
function newAllocationKey(): string {
  allocationKeySeed += 1;
  return `allocation-${allocationKeySeed}`;
}

/**
 * Record money received.
 *
 * WHAT THE ALLOCATIONS LEAVE UNALLOCATED IS CREDIT ON THE CLIENT'S ACCOUNT
 * (D153) — never an error, never rounded away. A payment recorded with no
 * allocation at all is a perfectly ordinary advance payment.
 */
export function PaymentFormPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();

  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [amount, setAmount] = useState<number | null>(null);
  const [receivedOn, setReceivedOn] = useState<string | null>(null);
  const [method, setMethod] = useState<string | null>('bank_transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [allocations, setAllocations] = useState<AllocationDraft[]>(() => {
    const invoiceId = searchParams.get('invoiceId');
    return invoiceId
      ? [{ key: newAllocationKey(), invoiceId, amount: null }]
      : [];
  });

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const openInvoices = useQuery({
    queryKey: ['invoices', 'open', clientId],
    queryFn: () =>
      invoicesApi.list({ clientId: clientId as string, status: 'issued', pageSize: 100 }),
    enabled: Boolean(clientId),
  });

  const invoicesWithBalance = (openInvoices.data?.data ?? []).filter(
    (invoice) => invoice.balanceMinor > 0,
  );

  const save = useMutation({
    mutationFn: () =>
      paymentsApi.record({
        clientId,
        amountMinor: Math.round((amount ?? 0) * 100),
        receivedOn,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(allocations.some((a) => a.invoiceId && a.amount)
          ? {
              allocations: allocations
                .filter((a) => a.invoiceId && a.amount)
                .map((a) => ({
                  invoiceId: a.invoiceId,
                  amountMinor: Math.round((a.amount as number) * 100),
                })),
            }
          : {}),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      show({ tone: 'success', message: t('payments.recorded') });
      navigate(`/payments/${created.id}`, { replace: true });
    },
  });

  const currency = invoicesWithBalance[0]?.currency;
  const usedInvoiceIds = new Set(allocations.map((a) => a.invoiceId).filter(Boolean));

  return (
    <>
      <PageHeader
        title={t('payments.recordTitle')}
        breadcrumbs={[
          { label: t('nav.payments'), to: '/payments' },
          { label: t('payments.recordTitle') },
        ]}
      />

      <div className="mb-stack">
        <FinanceMutationError error={save.error} />
      </div>

      {/* A form reads top to bottom: one capped column, as on every form page. */}
      <div className="w-full max-w-4xl">
        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <div className="border-t border-default-200 pt-stack">
              <ActionBar>
                <ActionButton priority="tertiary" onPress={() => navigate('/payments')}>
                  {t('common.cancel')}
                </ActionButton>
                <ActionButton
                  priority="primary"
                  type="submit"
                  isLoading={save.isPending}
                  isDisabled={!clientId || !amount || !receivedOn || !method}
                >
                  {t('common.save')}
                </ActionButton>
              </ActionBar>
            </div>
          }
        >
          <FormSection title={t('common.details')}>
            <SelectField
              label={t('payments.client')}
              placeholder={t('invoices.selectClient')}
              value={clientId}
              onChange={(value) => {
                setClientId(value);
                setAllocations([]);
              }}
              isRequired
              options={(clients.data?.data ?? []).map((client) => ({
                value: client.id,
                label: client.name ?? client.clientNumber,
                description: client.clientNumber,
              }))}
            />
            <NumberField
              label={t('payments.amount')}
              value={amount}
              onChange={setAmount}
              step="0.01"
              isRequired
              {...(currency ? { unit: currency } : {})}
            />
            <DateField
              label={t('payments.receivedOn')}
              value={receivedOn}
              onChange={setReceivedOn}
              isRequired
            />
            <SelectField
              label={t('payments.methodLabel')}
              value={method}
              onChange={setMethod}
              isRequired
              options={ALL_PAYMENT_METHODS.map((value) => ({
                value,
                label: t(`paymentMethod.${value}`),
              }))}
            />
            <TextField
              label={t('payments.reference')}
              description={t('payments.referenceHint')}
              value={reference}
              onChange={setReference}
            />
            <TextAreaField
              label={t('payments.note')}
              description={t('payments.noteHint')}
              value={note}
              onChange={setNote}
              rows={2}
            />
          </FormSection>

          <Panel
            title={t('payments.allocations')}
            description={t('payments.allocationsHint')}
            actions={
              clientId ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() =>
                    setAllocations((current) => [
                      ...current,
                      { key: newAllocationKey(), invoiceId: null, amount: null },
                    ])
                  }
                >
                  {t('payments.addAllocation')}
                </ActionButton>
              ) : null
            }
          >
            {!clientId ? (
              <p className="text-body text-default-600">
                {t('payments.chooseClientFirst')}
              </p>
            ) : (
              <div className="grid gap-stack">
                {allocations.map((allocation) => {
                  const invoice = invoicesWithBalance.find(
                    (candidate) => candidate.id === allocation.invoiceId,
                  );
                  return (
                    <div
                      key={allocation.key}
                      className="grid min-w-0 grid-cols-1 gap-stack rounded-medium border border-default-200 p-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
                    >
                      <SelectField
                        label={t('payments.invoice')}
                        value={allocation.invoiceId}
                        onChange={(value) =>
                          setAllocations((current) =>
                            current.map((candidate) =>
                              candidate.key === allocation.key
                                ? { ...candidate, invoiceId: value }
                                : candidate,
                            ),
                          )
                        }
                        options={invoicesWithBalance
                          .filter(
                            (candidate) =>
                              candidate.id === allocation.invoiceId ||
                              !usedInvoiceIds.has(candidate.id),
                          )
                          .map((candidate) => ({
                            value: candidate.id,
                            label: candidate.invoiceNumber ?? candidate.id,
                            description: formatMoney(candidate.balanceMinor, {
                              locale,
                              currency: candidate.currency,
                            }) ?? undefined,
                          }))}
                      />
                      <NumberField
                        label={t('payments.allocationAmount')}
                        value={allocation.amount}
                        onChange={(value) =>
                          setAllocations((current) =>
                            current.map((candidate) =>
                              candidate.key === allocation.key
                                ? { ...candidate, amount: value }
                                : candidate,
                            ),
                          )
                        }
                        step="0.01"
                        {...(invoice ? { unit: invoice.currency } : {})}
                      />
                      <div className="flex items-end">
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          onPress={() =>
                            setAllocations((current) =>
                              current.filter(
                                (candidate) => candidate.key !== allocation.key,
                              ),
                            )
                          }
                        >
                          {t('quotations.removeLine')}
                        </ActionButton>
                      </div>
                    </div>
                  );
                })}
                {allocations.length === 0 ? (
                  <p className="text-body text-default-600">
                    {t('payments.noAllocationsYet')}
                  </p>
                ) : null}
              </div>
            )}
          </Panel>
        </Form>
      </div>
    </>
  );
}
